import { useCallback, useEffect, useState } from 'react';
import { slotPolicyApi } from '../api/SlotPolicyApi';
import { SaveSlotPolicyRequest, SlotPolicyDto } from '../types/slotPolicy.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { useUserInfo } from '@/shared/hooks/useUserInfo';

export const useSlotManagement = () => {
    const { showToast } = useToast();
    const { getUserInfo } = useUserInfo();

    const [policies, setPolicies] = useState<SlotPolicyDto[]>([]);
    const [loading, setLoading] = useState(true);

    const isGlobalPolicy = (p: SlotPolicyDto) => !(p.standardAreas?.length);

    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        try {
            const res = await slotPolicyApi.getSlotPolicies();
            setPolicies(res.policies ?? []);
        } catch {
            showToast('슬롯 정책 목록 조회에 실패했습니다.', { type: ToastType.ERROR });
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    const savePolicy = useCallback(async (req: SaveSlotPolicyRequest): Promise<boolean> => {
        const userInfo = getUserInfo();
        if (!userInfo) return false;

        if (!req.standardAreas?.length) {
            const existing = policies.find(p => isGlobalPolicy(p));
            if (existing && existing.seqNo !== req.seqNo) {
                showToast('전체 협력사 적용(기본) 정책은 하나만 등록할 수 있습니다.', { type: ToastType.ERROR });
                return false;
            }
        }

        try {
            await slotPolicyApi.saveSlotPolicy({ ...req, userId: userInfo.userId });
            showToast(req.seqNo ? '수정되었습니다.' : '등록되었습니다.', { type: ToastType.SUCCESS });
            await fetchPolicies();
            return true;
        } catch (error: any) {
            const details = error?.originalError?.response?.data?.details;
            const detailMessages = details
                ? Object.values(details as Record<string, string>).join(', ')
                : '';
            const message = detailMessages || error?.message || '저장에 실패했습니다.';
            showToast(message, { type: ToastType.ERROR });
            return false;
        }
    }, [policies, getUserInfo, showToast, fetchPolicies]);

    const deletePolicy = useCallback(async (seqNo: number): Promise<boolean> => {
        try {
            await slotPolicyApi.deleteSlotPolicy(seqNo);
            showToast('삭제되었습니다.', { type: ToastType.SUCCESS });
            await fetchPolicies();
            return true;
        } catch {
            showToast('삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        }
    }, [showToast, fetchPolicies]);

    const hasGlobalPolicy = policies.some(isGlobalPolicy);

    return {
        policies,
        loading,
        hasGlobalPolicy,
        isGlobalPolicy,
        savePolicy,
        deletePolicy,
    };
};
